import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, DollarSign, Gift, Eye, Coins, TrendingUp, Sparkles, Percent, Target } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { TopFans, BestTimes } from "@/components/AnalyticsExtras";
import { TipGoalEditor } from "@/components/TipGoal";
import { FanPerkButtons, GiftPostDialog } from "@/components/FanPerks";
import { useAuth } from "@/context/AuthContext";

const GOLD = "#D4AF37";

function StatCard({ icon: Icon, value, label, delay = 0, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      data-testid={testid} className="pe-card rounded-2xl p-5">
      <Icon className="text-[#D4AF37] mb-2" size={22} />
      <div className="font-heading text-3xl gold-text">{value}</div>
      <div className="text-xs text-[#A0A0A0]">{label}</div>
    </motion.div>
  );
}

function ChartCard({ title, children, empty }) {
  return (
    <div className="pe-card rounded-2xl p-5">
      <div className="text-sm text-white font-medium mb-4">{title}</div>
      {empty ? (
        <div className="h-48 flex items-center justify-center text-xs text-[#A0A0A0]">No data yet</div>
      ) : (
        <div style={{ width: "100%", height: 200 }}>{children}</div>
      )}
    </div>
  );
}

const tooltipStyle = { background: "#1A1A1A", border: "1px solid #2A2A2A", borderRadius: 8, color: "#fff", fontSize: 12 };

export default function Analytics() {
  const [data, setData] = useState(null);
  const [goal, setGoal] = useState(null);
  const [giftFan, setGiftFan] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/creator/analytics").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/creator/tip-goal").then((r) => setGoal(r.data.goal)).catch(() => {});
  }, []);

  if (!data) return <div className="p-12 text-[#A0A0A0]">Loading…</div>;
  const s = data.summary;
  const conversion = s.total_views ? ((s.total_subscribers / s.total_views) * 100).toFixed(1) : "0.0";

  return (
    <div className="p-6 md:p-12 max-w-4xl mx-auto">
      <button data-testid="back-btn" onClick={() => navigate("/profile")} className="text-[#A0A0A0] hover:text-white flex items-center gap-2 mb-6">
        <ArrowLeft size={20} /> Back
      </button>
      <div className="overline mb-1">Creator dashboard</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-8 flex items-center gap-3"><TrendingUp className="text-[#D4AF37]" /> Analytics</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard testid="an-subs" icon={Users} value={s.total_subscribers} label="Subscribers" delay={0} />
        <StatCard testid="an-earnings" icon={DollarSign} value={`$${s.total_earnings.toFixed(2)}`} label="Total earnings" delay={0.05} />
        <StatCard testid="an-tips" icon={Gift} value={`$${s.total_tips.toFixed(2)}`} label="Cash tips" delay={0.1} />
        <StatCard testid="an-coins" icon={Coins} value={s.coin_tips} label="Coin tips" delay={0.15} />
        <StatCard testid="an-views" icon={Eye} value={s.total_views} label="Post views" delay={0.2} />
        <StatCard testid="an-posts" icon={TrendingUp} value={s.total_posts} label="Posts" delay={0.25} />
        <StatCard testid="an-gift-coins" icon={Sparkles} value={s.gift_coins || 0} label="Gift coins" delay={0.3} />
        <StatCard testid="an-conversion" icon={Percent} value={conversion} label="Subscribers per 100 views" delay={0.35} />
      </div>

      <div className="pe-card rounded-2xl p-5 mb-5" data-testid="tip-goal-card">
        <div className="text-sm text-white font-medium mb-3 flex items-center gap-2"><Target size={15} className="text-[#D4AF37]" /> Live tip goal</div>
        <TipGoalEditor goal={goal} onSaved={setGoal} />
      </div>

      <GiftPostDialog fan={giftFan} creatorId={user?.id} onClose={() => setGiftFan(null)} />
      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <TopFans fans={data.top_fans || []} renderActions={(f) => <FanPerkButtons fan={f} onGift={setGiftFan} />} />
        <BestTimes activity={data.post_activity || []} />
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <ChartCard title="Subscribers over time" empty={data.subscribers_over_time.length === 0}>
          <ResponsiveContainer>
            <AreaChart data={data.subscribers_over_time} margin={{ left: -20 }}>
              <defs>
                <linearGradient id="gSub" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GOLD} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#222" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#777", fontSize: 10 }} />
              <YAxis tick={{ fill: "#777", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="subscribers" stroke={GOLD} fill="url(#gSub)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Earnings over time ($)" empty={data.earnings_over_time.length === 0}>
          <ResponsiveContainer>
            <BarChart data={data.earnings_over_time} margin={{ left: -20 }}>
              <CartesianGrid stroke="#222" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#777", fontSize: 10 }} />
              <YAxis tick={{ fill: "#777", fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(212,175,55,0.08)" }} />
              <Bar dataKey="earnings" fill={GOLD} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <ChartCard title="Cash tips over time ($)" empty={data.tips_over_time.length === 0}>
          <ResponsiveContainer>
            <BarChart data={data.tips_over_time} margin={{ left: -20 }}>
              <CartesianGrid stroke="#222" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#777", fontSize: 10 }} />
              <YAxis tick={{ fill: "#777", fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(212,175,55,0.08)" }} />
              <Bar dataKey="tips" fill="#E63946" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="pe-card rounded-2xl p-5">
          <div className="text-sm text-white font-medium mb-4">Top posts by views</div>
          {data.top_posts.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-xs text-[#A0A0A0]">No posts yet</div>
          ) : (
            <div className="space-y-3">
              {data.top_posts.map((p, i) => (
                <div key={i} data-testid={`top-post-${i}`} className="flex items-center gap-3">
                  <span className="font-heading gold-text w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{p.caption}</div>
                    {p.is_premium && <span className="text-[10px] text-[#D4AF37]">Premium</span>}
                  </div>
                  <div className="text-sm text-[#A0A0A0] flex items-center gap-1"><Eye size={13} /> {p.views}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
